import React from "react";
import PropTypes from "prop-types";
import { defineMessages, injectIntl } from "react-intl";
import DeleteIcon from "@material-ui/icons/Delete";
import { connect } from "react-redux";
import { sendSessionMedia } from "../../../store/actions";

import "./index.sass";

import Modal from "../../Atoms/Modal";
import Photo from "../../Atoms/Photo";
import Video from "../../Atoms/Video";
import Arrow from "../../Atoms/Arrow";
import Check from "../../Atoms/Check";
import PhotoDownload from "../../Atoms/PhotoDownload";
import IconButton from "../../Atoms/IconButton";
import Loader from "../../Atoms/Loader";
import Tooltip from "../../Atoms/ToolTip";

import { Order } from "../../../shared";
import Text from "../../Atoms/Text";
import Cart from "../Cart";
import MediaProtectionGuard from "../../Molecules/MediaProtectionGuard";

/* global document */

class MediaModal extends React.Component {
	constructor(props) {
		super(props);
		this.handleKeyDown = this.handleKeyDown.bind(this);
		this.state = {
			originalMedia: null,
			error: null,
			loading: false,
		};
	}

	componentDidMount() {
		document.addEventListener("keydown", this.handleKeyDown, false);
	}

	componentWillUnmount() {
		document.removeEventListener("keydown", this.handleKeyDown);
	}

	componentDidUpdate(prevProps) {
		const { isOpened, canDownloadMedia, media = {} } = this.props;
		const { id } = media || {};
		const { originalMedia, error, loading } = this.state;
		const { media: lastMedia = {} } = prevProps;
		const { id: lastId } = lastMedia || {};
		const notPhotoLoaded = !originalMedia && !loading && !error;
		const changedPhoto = !id || id !== lastId;

		if (
			isOpened &&
			(canDownloadMedia || media.canDownload) &&
			(notPhotoLoaded || changedPhoto)
		)
			this.getOriginalPhoto();
	}

	getOriginalPhoto = () => {
		const { media, isExtra } = this.props;
		this.setState({
			originalMedia: `${process.env.ROVERPIX_API}/api/v1/${isExtra ? "extra" : media.type
				}/download/${media.id}/${media.filename}`
		})
	};

	handleKeyDown(e) {
		const { onBefore } = this.props;
		const { onNext } = this.props;

		if (e.code === "ArrowLeft") {
			onBefore();
		}

		if (e.code === "ArrowRight") {
			onNext();
		}
	}

	render() {
		const {
			className,
			media,
			order,
			isOpened,
			canDownloadMedia,
			canRemoveMedia,
			isExtra,
			onClose,
			onBefore,
			onNext,
			intl,
			onAddToOrder,
			onDownloadMedia,
			onDownloadComplete,
			onRemoveMedia,
			hasBeenVisible,
			handleTooltipClose,
			cardCode,
			canBuy,
		} = this.props;
		const { loading, error, originalMedia } = this.state;
		const { formatMessage } = intl;
		const isProtected = !(canDownloadMedia || media.canDownload);
		const messages = defineMessages({
			download: {
				id: "media.modal.download",
				defaultMessage: "Fazer o download",
			},
			tooltipTitle: {
				id: "ToolTip.text",
				defaultMessage:
					"Para visualizar as mídias adquiridas sem marca d'agua, faça o download pelo botão esquerdo e/ou compartilhe pelo direito.",
			},
			totalPhotos: {
				id: "cartPrice.totalPhoto",
				defaultMessage: "Total de fotos",
			},
			total: {
				id: "cart.total",
				defaultMessage: "TOTAL",
			},
		});

		return (
			<>
			<MediaProtectionGuard active={isOpened && isProtected} />
			<Modal
				className={`MediaModal ${canBuy ? 'MediaModal--with-cart' : ''} ${className}`}
				isOpened={isOpened}
				onClose={onClose}
			>
				<div className="MediaModal__container">
					{media.id && media.filename && media.type === "photo" && (
						<Photo
							className="MediaModal__photo"
							id={media.id}
							filename={media.filename}
							source={media.preview_path}
							type="preview"
							isExtra={isExtra}
							protectedMedia={isProtected}
						/>
					)}
					{media.id && media.filename && media.type === "video" && (
						<Video
							className="MediaModal__video"
							id={media.id}
							filename={media.filename}
							source={media.preview_path}
							type="preview"
							isExtra={isExtra}
							protectedMedia={isProtected}
						/>
					)}
					<Arrow
						className="MediaModal__arrow MediaModal__arrow--right"
						size="small"
						onClick={onNext}
						direction="right"
						active
					/>
					<Arrow
						className="MediaModal__arrow MediaModal__arrow--left"
						size="small"
						direction="left"
						onClick={onBefore}
						active
					/>
				</div>
				{canDownloadMedia || media.canDownload ? (
					<div className="MediaModal__actions">
						{loading && <Loader size="x-small" />}
						{!error && !loading && originalMedia && (
							<>
								<div className="MediaModal__actions__download">
									<Tooltip className="MediaModal__actions__tooltip" onClick={handleTooltipClose} hasBeenVisible={hasBeenVisible} text={formatMessage(messages.tooltipTitle)}/>
									<div>
										<PhotoDownload
											className="MediaModal__actions__download__button"
											href={originalMedia}
											filename={media.filename}
											onDownload={() => {
												onDownloadMedia();
												this.setState({ loading: true });
											}}
											onDownloadComplete={() => {
												onDownloadComplete();
												this.setState({ loading: false });
												const { order, media, sendSessionMedia } = this.props;
												if (order && order.id && media && media.id) {
													sendSessionMedia(order.id, { photos: [media.id], videos: [] });
												}
											}}
											loading={loading}
										/>
									</div>
								</div>
							</>
						)}
					</div>
				) : (
					<Check
						className="MediaModal__check"
						active={Order.isMediaOnOrder(media.id, order)}
						onClick={(e) => onAddToOrder(e, media)}
					/>
				)}
				{canBuy && (
					<div className="MediaModal__mobile-cart">
						<Cart cardCode={cardCode} onCloseMobileCart={onClose} />
					</div>
				)}
				<div className="MediaModal__edit">
					{canRemoveMedia && (
						<IconButton rounded onClick={() => onRemoveMedia(media.id)}>
							<DeleteIcon fill="white" />
						</IconButton>
					)}
				</div>
			</Modal>
			</>
		);
	}
}

MediaModal.propTypes = {
	className: PropTypes.string,
	source: PropTypes.string,
	media: PropTypes.shape(),
	order: PropTypes.shape(),
	isOpened: PropTypes.bool,
	canDownloadMedia: PropTypes.bool,
	canRemoveMedia: PropTypes.bool,
	canBuy: PropTypes.bool,
	isExtra: PropTypes.bool,
	intl: PropTypes.object.isRequired,
	onAddToOrder: PropTypes.func,
	onClose: PropTypes.func,
	onBefore: PropTypes.func,
	onNext: PropTypes.func,
	onDownloadMedia: PropTypes.func,
	onDownloadComplete: PropTypes.func,
	onRemoveMedia: PropTypes.func,
	cardCode: PropTypes.string,
};

MediaModal.defaultProps = {
	className: "",
	source: "",
	photo: {},
	order: {},
	isOpened: false,
	canDownloadMedia: false,
	canRemoveMedia: false,
	canBuy: false,
	isExtra: false,
	onAddToOrder: () => {},
	onClose: () => {},
	onBefore: () => {},
	onNext: () => {},
	onDownloadMedia: () => {},
	onDownloadComplete: () => {},
	onRemoveMedia: () => {},
	cardCode: "",
};

const mapStateToProps = (state, ownProps) => ({
	order: ownProps.order,
	media: ownProps.media,
});

const mapDispatchToProps = (dispatch) => ({
	sendSessionMedia: (orderId, medias) => dispatch(sendSessionMedia(orderId, medias)),
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(MediaModal));
